import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import Icon from "../../components/em/Icon";
import { Plane } from "lucide-react";
import LeaveTypeModal from "../../components/modals/LeaveTypeModal";
import { PageHeader, PrimaryButton, Tabs } from "../../components/em/ui";
import { API_URL } from "../../api/api";
import { hasPermission } from "../../utils/permissionUtils";
import "./em-leaves.css";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
// Local YYYY-MM-DD (no timezone shift like toISOString would do).
const toLocalDate = (d = new Date()) =>
  d.getFullYear() +
  "-" +
  String(d.getMonth() + 1).padStart(2, "0") +
  "-" +
  String(d.getDate()).padStart(2, "0");

// Status canonicalised lowercase. Display capitalised.
const statusLabel = (s) => {
  const v = String(s || "").toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
};
const isStatus = (row, target) =>
  String(row?.status || "").toLowerCase() === String(target).toLowerCase();

const PAGE_SIZES = [25, 50, 100];

const LeaveView = () => {
  const baseURL = `${API_URL}/api`;
  const role = String(localStorage.getItem("role") || "").toLowerCase();
  const isAdminOrHR = role === "admin" || role === "hr";
  // A team lead granted `approve_team_leaves`. The server already limits
  // /leaves/all to their own team, so any team row that arrives here is one
  // they are allowed to act on — and it re-checks on approve/reject anyway.
  const canApproveTeam = hasPermission("approve_team_leaves");
  const userdata = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userdata") || "{}");
    } catch {
      return {};
    }
  }, []);
  const myUserId = Number(userdata?.id) || null;
  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token],
  );
  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.assign("/login");
      return true;
    }
    return false;
  }, []);

  // Tabs (Q11=c). Members default to My; admin/HR default to Pending.
  const [activeTab, setActiveTab] = useState(isAdminOrHR ? "pending" : "my");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [balance, setBalance] = useState([]);
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(null); // {id, action: 'approved'|'rejected'}
  const [showBulkModal, setShowBulkModal] = useState(null); // 'approved'|'rejected'
  const [actionComment, setActionComment] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deptList, setDeptList] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "",
    department: "",
    from: "",
    to: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const emptyForm = {
    leave_type_id: "",
    start_date: "",
    end_date: "",
    duration_type: "full_day",
    reason: "",
  };
  const [form, setForm] = useState(emptyForm);
  // Server-computed day count + remaining allowance for the leave being drafted.
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);

  /* ---------------- LOAD DATA ---------------- */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reqs = [
        axios.get(`${baseURL}/settings/calendar/leave-types`, authHeader),
        axios.get(`${baseURL}/leaves/all`, authHeader),
      ];
      if (myUserId) {
        reqs.push(
          axios
            .get(`${baseURL}/leaves/balance/${myUserId}`, authHeader)
            .catch(() => ({
              data: { balances: [] },
            })),
        );
      }
      const [typesRes, listRes, balRes] = await Promise.all(reqs);
      setLeaveTypes(typesRes.data || []);
      setLeaveRequests(listRes.data || []);
      setBalance(balRes?.data?.balances || []);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.error || "Failed to load leaves.");
    } finally {
      setLoading(false);
    }
  }, [baseURL, authHeader, myUserId, handleAuthError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ---------------- FORM ---------------- */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      // Half-day → end_date follows start_date.
      if (name === "duration_type") {
        if (value !== "full_day") {
          return {
            ...prev,
            duration_type: value,
            end_date: prev.start_date || "",
          };
        }
        return { ...prev, duration_type: value };
      }
      if (name === "start_date" && prev.duration_type !== "full_day") {
        return { ...prev, start_date: value, end_date: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const openApplyModal = () => {
    setForm(emptyForm);
    setSubmitErr(null);
    setQuota(null);
    setShowApplyModal(true);
  };

  // Ask the server how many working days the drafted leave costs and how much
  // of the type's annual allowance is left. Debounced; the same computation
  // gates the POST, so the warning here matches the refusal there.
  const {
    leave_type_id: draftType,
    start_date: draftStart,
    end_date: draftEnd,
    duration_type: draftDuration,
  } = form;
  useEffect(() => {
    if (
      !showApplyModal ||
      !draftType ||
      !draftStart ||
      !draftEnd ||
      draftStart > draftEnd
    ) {
      setQuota(null);
      setQuotaLoading(false);
      return undefined;
    }
    let cancelled = false;
    setQuotaLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${baseURL}/leaves/preview`, {
          ...authHeader,
          params: {
            leave_type_id: draftType,
            start_date: draftStart,
            end_date: draftEnd,
            duration_type: draftDuration,
          },
        });
        if (!cancelled) setQuota(res.data);
      } catch {
        // Non-fatal — submitting still gets the authoritative check.
        if (!cancelled) setQuota(null);
      } finally {
        if (!cancelled) setQuotaLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    showApplyModal,
    draftType,
    draftStart,
    draftEnd,
    draftDuration,
    baseURL,
    authHeader,
  ]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!form.leave_type_id) return setSubmitErr("Pick a leave type.");
    if (!form.start_date || !form.end_date)
      return setSubmitErr("Start and end dates are required.");
    if (new Date(form.start_date) > new Date(form.end_date))
      return setSubmitErr("Start date must be on or before end date.");
    // Mirrors of the server rules, for immediate feedback. The POST is still
    // checked server-side — these compare against dates the client supplied.
    if (quota?.notice_ok === false) return setSubmitErr(quota.notice_message);
    if (quota?.exceeds) return setSubmitErr(quota.message);
    try {
      await axios.post(
        `${baseURL}/leaves/request`,
        {
          leave_type_id: form.leave_type_id,
          duration_type: form.duration_type,
          start_date: form.start_date,
          end_date: form.end_date,
          reason: form.reason,
        },
        authHeader,
      );
      setShowApplyModal(false);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setSubmitErr(err?.response?.data?.error || "Could not submit request.");
    }
  };

  const deptMap = useMemo(
    () => Object.fromEntries(deptList.map((d) => [String(d.id), d.name])),
    [deptList],
  );

  // Department may arrive already resolved (department_name), as a numeric
  // departments.id, or as free text — try each in that order.
  const departmentName = useCallback(
    (r) =>
      r?.department_name || deptMap[String(r?.department)] || r?.department || "",
    [deptMap],
  );

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const dep = await axios.get(
          `${API_URL}/api/settings/departments`,
          authHeader,
        );
        setDeptList(Array.isArray(dep.data) ? dep.data : []);
      } catch (err) {
        if (err) return;
      }
    };
    fetchDepts();
  }, []);

  /* ---------------- ACTIONS ---------------- */
  const openActionModal = (id, action) => {
    setActionComment("");
    setShowActionModal({ id, action });
  };

  const submitAction = async () => {
    if (!showActionModal) return;
    setBusyAction(true);
    try {
      await axios.put(
        `${baseURL}/leaves/status/${showActionModal.id}`,
        { status: showActionModal.action, comment: actionComment || null },
        authHeader,
      );
      setShowActionModal(null);
      setActionComment("");
      fetchAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Could not update status.");
    } finally {
      setBusyAction(false);
    }
  };

  const submitBulkAction = async () => {
    if (!showBulkModal || !selected.size) return;
    setBusyAction(true);
    const ids = Array.from(selected);
    const failures = [];
    try {
      await Promise.all(
        ids.map((id) =>
          axios
            .put(
              `${baseURL}/leaves/status/${id}`,
              { status: showBulkModal, comment: actionComment || null },
              authHeader,
            )
            .catch((err) =>
              failures.push({
                id,
                msg: err?.response?.data?.error || err.message,
              }),
            ),
        ),
      );
      setShowBulkModal(null);
      setActionComment("");
      setSelected(new Set());
      fetchAll();
      if (failures.length) {
        toast.error(
          `${ids.length - failures.length} updated, ${failures.length} failed.\n` +
            failures.map((f) => `#${f.id}: ${f.msg}`).join("\n"),
        );
      }
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.message || "Bulk update failed.");
    } finally {
      setBusyAction(false);
    }
  };

  const withdrawLeave = async (id) => {
    if (!(await confirmDialog("Withdraw this pending leave request?"))) return;
    try {
      await axios.put(`${baseURL}/leaves/${id}/withdraw`, {}, authHeader);
      fetchAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Could not withdraw leave.");
    }
  };

  // Switching to My Leaves drops a carried-over status filter — its control is
  // hidden there, so leaving one set would silently empty the table.
  const handleTabChange = (key) => {
    if (key === "my") setFilters((f) => (f.status ? { ...f, status: "" } : f));
    setActiveTab(key);
  };

  /* ---------------- DERIVED ---------------- */
  // All Leaves is a read-only record — approve/reject/withdraw (and the bulk
  // selection that drives them) live on the other two tabs.
  const showActions = activeTab !== "all";

  // The visible rows depend on the active tab AND the filters.
  const tabRows = useMemo(() => {
    if (activeTab === "my") {
      // Approved leaves only — pending ones live in the Pending Approvals tab,
      // which is where a member withdraws them.
      return leaveRequests.filter(
        (r) => Number(r.user_id) === myUserId && isStatus(r, "approved"),
      );
    }
    if (activeTab === "pending") {
      // Admin/HR see pending across the org; a team lead sees their team's
      // (the server has already narrowed the payload to that team); everyone
      // else sees only their own.
      return leaveRequests.filter(
        (r) =>
          isStatus(r, "pending") &&
          (isAdminOrHR || canApproveTeam || Number(r.user_id) === myUserId),
      );
    }
    return leaveRequests; // 'all'
  }, [activeTab, leaveRequests, isAdminOrHR, canApproveTeam, myUserId]);

  const filteredRows = useMemo(() => {
    let data = tabRows;
    const q = filters.search.trim().toLowerCase();
    if (q) {
      data = data.filter(
        (r) =>
          (r.reason || "").toLowerCase().includes(q) ||
          (r.employee_name || "").toLowerCase().includes(q) ||
          String(r.user_id).includes(q),
      );
    }
    if (filters.status) data = data.filter((r) => isStatus(r, filters.status));
    if (filters.type)
      data = data.filter(
        (r) => Number(r.leave_type_id) === Number(filters.type),
      );
    if (filters.department)
      data = data.filter(
        (r) => String(r.department || "") === filters.department,
      );
    if (filters.from)
      data = data.filter(
        (r) => String(r.end_date).slice(0, 10) >= filters.from,
      );
    if (filters.to)
      data = data.filter(
        (r) => String(r.start_date).slice(0, 10) <= filters.to,
      );
    return data;
  }, [tabRows, filters]);

  // Reset page when tab/filter changes so we never paginate off the end.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [activeTab, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const formatDate = (s) => (s ? String(s).slice(0, 10) : "");
  const dayCount = (r) => {
    if (!r.start_date || !r.end_date) return 0;
    if (String(r.duration_type || "").startsWith("half")) return 0.5;
    const ms =
      new Date(r.end_date).setHours(0, 0, 0, 0) -
      new Date(r.start_date).setHours(0, 0, 0, 0);
    return Math.max(1, Math.round(ms / 86400000) + 1);
  };

  /* ---------------- BULK ---------------- */
  const allOnPagePending = visibleRows.filter((r) => isStatus(r, "pending"));
  const allSelected =
    allOnPagePending.length > 0 &&
    allOnPagePending.every((r) => selected.has(r.id));
  const togglePageSelection = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) allOnPagePending.forEach((r) => next.delete(r.id));
      else allOnPagePending.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ---------------- EXPORT ---------------- */
  const exportCSV = () => {
    const cols = [
      ["Employee", (r) => r.employee_name],
      ["Department", (r) => departmentName(r)],
      ["Type", (r) => r.leave_type],
      ["Start", (r) => formatDate(r.start_date)],
      ["End", (r) => formatDate(r.end_date)],
      ["Days", (r) => dayCount(r)],
      ["Status", (r) => statusLabel(r.status)],
      ["Reason", (r) => r.reason],
      ["Comment", (r) => r.approver_comment || r.reject_reason || ""],
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => escape(c[0])).join(",");
    const body = filteredRows
      .map((r) => cols.map((c) => escape(c[1](r))).join(","))
      .join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `leaves-${toLocalDate()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /* ---------------- RENDER HELPERS ---------------- */
  const StatusPill = ({ value }) => {
    const v = String(value || "").toLowerCase();
    return (
      <span className={`leave-status-pill leave-status-${v}`}>
        {statusLabel(v)}
      </span>
    );
  };
  StatusPill.propTypes = { value: PropTypes.string };

  const RowActions = ({ row }) => {
    // NOTE: `row.primary_manager_id` is a team_members.id while myUserId is a
    // users.id, so that comparison only matches when the two ids coincide. It
    // is kept as-is rather than silently changed; `canApproveTeam` is the path
    // that actually works, and the server re-checks team membership on the
    // approve/reject call regardless.
    const canManage =
      isAdminOrHR || canApproveTeam || Number(row.primary_manager_id) === myUserId;
    const isOwner = Number(row.user_id) === myUserId;
    if (!isStatus(row, "pending")) return <span className="muted">—</span>;
    return (
      <div className="row-actions">
        {canManage && Number(row.user_id) !== myUserId && (
          <>
            <button
              className="btn-approve"
              onClick={() => openActionModal(row.id, "approved")}
            >
              Approve
            </button>
            <button
              className="btn-reject"
              onClick={() => openActionModal(row.id, "rejected")}
            >
              Reject
            </button>
          </>
        )}
        {isOwner && (
          <button
            className="btn-withdraw"
            onClick={() => withdrawLeave(row.id)}
          >
            Withdraw
          </button>
        )}
      </div>
    );
  };
  RowActions.propTypes = { row: PropTypes.object.isRequired };

  /* ---------------- UI ---------------- */
  return (
    <div
      className="leave-management em-leaves"
      style={{ maxWidth: 1440, margin: "0 auto", padding: "0 32px 48px" }}
    >
      <LeaveTypeModal
        isOpen={showLeaveTypeModal}
        onClose={() => setShowLeaveTypeModal(false)}
        onSaved={fetchAll}
      />

      <PageHeader
        icon={Plane}
        title="Leave Management"
        subtitle="Apply for leave, track balance, approve requests."
        actions={
          <PrimaryButton icon={Plane} onClick={openApplyModal}>
            Apply for Leave
          </PrimaryButton>
        }
      />

      {/* Balance cards — current user's quota */}
      {balance.length > 0 && (
        <div className="leave-cards">
          {balance.map((b) => {
            const total = Number(b.max_days_per_year || 0);
            const used = Number(b.used_days || 0);       // approved
            const pending = Number(b.pending_days || 0); // awaiting a decision
            const committed = Number(b.committed_days ?? used + pending);
            // The SERVER's remaining, not `total - used` recomputed here. That
            // recomputation ignored pending entirely, so a freshly filed
            // request left the card unchanged — and it could disagree with the
            // quota rule that actually blocks the next request.
            const remaining =
              b.remaining_days != null ? Number(b.remaining_days) : total - committed;
            const pctUsed = total > 0 ? Math.min(100, (used / total) * 100) : 0;
            const pctPending =
              total > 0 ? Math.min(100 - pctUsed, (pending / total) * 100) : 0;
            const color = b.color || "#94a3b8";
            return (
              <div key={b.leave_type_id} className="leave-card">
                <h4>
                  <span className="dot" style={{ background: color }} />
                  {b.name}
                </h4>
                <div className="leave-card-num" style={{ color }}>
                  {remaining}
                </div>
                <p className="leave-card-sub">of {total} remaining</p>
                {/* Two segments: approved solid, pending faded. The bar reads
                    as one committed block while still showing how much of it
                    could come back if a request is withdrawn or rejected. */}
                <div className="leave-card-bar">
                  <span style={{ width: `${pctUsed}%`, background: color }} />
                  {pending > 0 && (
                    <span
                      style={{ width: `${pctPending}%`, background: color, opacity: 0.4 }}
                      title={`${pending} day(s) awaiting approval`}
                    />
                  )}
                </div>
                <p className="leave-card-foot">
                  {used} used
                  {pending > 0 && ` · ${pending} pending`} · {remaining} left
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs — em pill segmented control */}
      <div className="tabHead">
        <Tabs
          tabs={[
            { key: "my", label: "My Leaves" },
            { key: "pending", label: "Pending Approvals" },
            ...(isAdminOrHR ? [{ key: "all", label: "All Leaves" }] : []),
          ]}
          active={activeTab}
          onChange={handleTabChange}
        />
      </div>

      {error && (
        <div role="alert" className="leave-error-banner">
          ⚠ {error}
        </div>
      )}

      <div className="card">
        <div className="title-btns">
          <h3>
            {activeTab === "my"
              ? "My Leaves"
              : activeTab === "pending"
                ? "Pending Approvals"
                : "All Leaves"}{" "}
            <span className="count">({filteredRows.length})</span>
          </h3>
          <div>
            <button onClick={exportCSV}>Export CSV</button>
          </div>
        </div>

        {/* Filters */}
        <div className="leave-filters">
          <input
            placeholder="Search by employee, reason…"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
          />
          {/* My Leaves is approved-only, so a status filter there can only
              ever narrow to nothing. */}
          {activeTab !== "my" && (
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          )}
          <select
            value={filters.type}
            onChange={(e) =>
              setFilters((f) => ({ ...f, type: e.target.value }))
            }
          >
            <option value="">All types</option>
            {leaveTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {isAdminOrHR && (
            <select
              value={filters.department}
              onChange={(e) =>
                setFilters((f) => ({ ...f, department: e.target.value }))
              }
            >
              <option value="">All departments</option>
              {deptList.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
          <input
            type="date"
            value={filters.from}
            onChange={(e) =>
              setFilters((f) => ({ ...f, from: e.target.value }))
            }
            aria-label="From"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            aria-label="To"
          />
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && isAdminOrHR && showActions && (
          <div className="leave-bulk-bar">
            <Icon name="ok" /> <b>{selected.size}</b> selected
            <span style={{ flex: 1 }} />
            <button
              className="btn-approve"
              onClick={() => setShowBulkModal("approved")}
            >
              Bulk approve
            </button>
            <button
              className="btn-reject"
              onClick={() => setShowBulkModal("rejected")}
            >
              Bulk reject
            </button>
            <button onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {loading ? (
          <p style={{ padding: 24, color: "#64748b" }}>Loading leaves…</p>
        ) : filteredRows.length === 0 ? (
          <p style={{ padding: 24, color: "#64748b", textAlign: "center" }}>
            No leave requests match your filters.
          </p>
        ) : (
          <div className="resp-table">
            <table className="leave-table">
              <thead>
                <tr>
                  {isAdminOrHR && showActions && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={togglePageSelection}
                        aria-label="Select all on page"
                      />
                    </th>
                  )}
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Reason</th>
                  {showActions && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.id}>
                    {isAdminOrHR && showActions && (
                      <td>
                        {isStatus(r, "pending") && (
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggleRow(r.id)}
                            aria-label={`Select leave #${r.id}`}
                          />
                        )}
                      </td>
                    )}
                    <td>{r.employee_name || "—"}</td>
                    <td>{departmentName(r) || "—"}</td>
                    <td>{r.leave_type || "—"}</td>
                    <td>{formatDate(r.start_date)}</td>
                    <td>{formatDate(r.end_date)}</td>
                    <td>{dayCount(r)}</td>
                    <td>
                      <StatusPill value={r.status} />
                      {r.reject_reason && (
                        <small
                          style={{
                            display: "block",
                            color: "#b91c1c",
                            marginTop: 2,
                          }}
                          title={r.reject_reason}
                        >
                          ⚠ {r.reject_reason.slice(0, 40)}
                          {r.reject_reason.length > 40 ? "…" : ""}
                        </small>
                      )}
                    </td>
                    <td title={r.reason || ""}>
                      {(r.reason || "").slice(0, 40)}
                      {r.reason && r.reason.length > 40 ? "…" : ""}
                    </td>
                    {showActions && (
                      <td>
                        <RowActions row={r} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredRows.length > pageSize && (
          <div className="leave-pagination">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Apply for Leave</h3>
            <form onSubmit={handleApplyLeave}>
              <div className="form-item required">
                <label>Leave Type</label>
                <select
                  name="leave_type_id"
                  value={form.leave_type_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select type</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-item required">
                <label>Duration Type</label>
                <select
                  name="duration_type"
                  value={form.duration_type}
                  onChange={handleFormChange}
                >
                  <option value="full_day">Full Day</option>
                  <option value="half_day_first">First Half</option>
                  <option value="half_day_second">Second Half</option>
                </select>
              </div>
              <div className="form-item required">
                <label>Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              {form.duration_type === "full_day" && (
                <div className="form-item required">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleFormChange}
                    min={form.start_date || undefined}
                    required
                  />
                </div>
              )}
              <div className="form-item">
                <label>Reason</label>
                <textarea
                  name="reason"
                  value={form.reason}
                  onChange={handleFormChange}
                  placeholder="Optional reason"
                  rows={3}
                />
              </div>
              {/* Allowance feedback — week-off/holiday aware day count and
                  what's left of this leave type for the year. */}
              {quotaLoading && (
                <p className="leave-quota-hint">Checking your balance…</p>
              )}
              {!quotaLoading && quota && !quota.exceeds && quota.notice_ok !== false && (
                <p className="leave-quota-hint">
                  {quota.requested_days} day(s) counted
                  {quota.remaining_days != null &&
                    ` · ${quota.remaining_days} of ${quota.max_days_per_year} left for ${quota.year}`}
                  {quota.min_advance_notice_days > 0 &&
                    ` · needs ${quota.min_advance_notice_days} day(s) notice`}
                </p>
              )}
              {/* Advance notice first: it is the one the employee can fix by
                  moving the dates, where an exhausted allowance cannot be. */}
              {!quotaLoading && quota?.notice_ok === false && (
                <p role="alert" className="leave-quota-warning">
                  ⚠ {quota.notice_message}
                </p>
              )}
              {!quotaLoading && quota?.exceeds && quota.notice_ok !== false && (
                <p role="alert" className="leave-quota-warning">
                  ⚠ {quota.message}
                </p>
              )}
              {submitErr && <p className="error">{submitErr}</p>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="org-gradi"
                  disabled={!!quota?.exceeds || quota?.notice_ok === false}
                  title={
                    quota?.notice_ok === false
                      ? quota.notice_message
                      : quota?.exceeds
                        ? quota.message
                        : undefined
                  }
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve / Reject single modal */}
      {showActionModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <h3>
              {showActionModal.action === "approved"
                ? "Approve leave"
                : "Reject leave"}
            </h3>
            <p style={{ color: "#64748b", marginTop: -6 }}>
              {showActionModal.action === "approved"
                ? "Optional comment shown to the employee."
                : "Add a reason — the employee will see this."}
            </p>
            <div className="form-item">
              <label>
                {showActionModal.action === "approved" ? "Comment" : "Reason"}
              </label>
              <textarea
                rows={3}
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder={
                  showActionModal.action === "approved"
                    ? "(optional)"
                    : "Why are you rejecting?"
                }
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowActionModal(null)}
                disabled={busyAction}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  showActionModal.action === "approved"
                    ? "btn-approve"
                    : "btn-reject"
                }
                onClick={submitAction}
                disabled={busyAction}
              >
                {busyAction
                  ? "Saving…"
                  : showActionModal.action === "approved"
                    ? "Approve"
                    : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Approve / Reject modal */}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <h3>
              Bulk {showBulkModal === "approved" ? "approve" : "reject"} (
              {selected.size})
            </h3>
            <p style={{ color: "#64748b", marginTop: -6 }}>
              {showBulkModal === "approved"
                ? "Optional comment applied to all selected requests."
                : "Reason will be saved on every selected request."}
            </p>
            <div className="form-item">
              <label>
                {showBulkModal === "approved" ? "Comment" : "Reason"}
              </label>
              <textarea
                rows={3}
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder={
                  showBulkModal === "approved" ? "(optional)" : "Reason"
                }
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowBulkModal(null)}
                disabled={busyAction}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  showBulkModal === "approved" ? "btn-approve" : "btn-reject"
                }
                onClick={submitBulkAction}
                disabled={busyAction}
              >
                {busyAction
                  ? "Saving…"
                  : showBulkModal === "approved"
                    ? `Approve ${selected.size}`
                    : `Reject ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveView;
